import React from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Mail, Phone, MessageCircle, MapPin, Linkedin, Store, Apple } from "lucide-react";
import { ASSETS, NAV_LINKS } from "@/data/content";
import { goToAnchor } from "@/lib/nav";
import { CONTACT, FOUNDER, STORES, LEGAL_OPERATOR } from "@/data/company";

const LEGAL_LINKS = [
  { label: "Terms & Conditions", to: "/terms-and-conditions" },
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Refund & Dispute Policy", to: "/refund-and-dispute-policy" },
  { label: "Delete Account & Data", to: "/delete-account-data" },
];

export const Footer = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleLink = (link) => {
    if (link.type === "dropdown") navigate(link.items[0].to);
    else if (link.type === "route") navigate(link.to);
    else goToAnchor(navigate, pathname, link.href);
  };

  const linkCls = "text-left text-sm font-medium text-slate-500 hover:text-[#091630] transition-colors";

  return (
    <footer data-testid="footer" className="relative border-t border-slate-100 bg-white py-14">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1.1fr_1.4fr] gap-12 items-start">
          {/* Brand + contact */}
          <div>
            <Link to="/" data-testid="footer-logo" className="flex items-center">
              <img src={ASSETS.logo} alt="WorkPay" loading="lazy" decoding="async" className="h-12 w-auto" />
            </Link>
            <p className="mt-4 text-sm text-slate-500 max-w-md leading-relaxed">
              A vendor-first, Stripe-powered project payment app for service businesses. Start every project
              with clarity.
            </p>
            <div className="mt-5 space-y-2.5">
              <a href={`mailto:${CONTACT.email}`} data-testid="footer-email" className="flex items-center gap-2 text-sm font-semibold text-[#091630] hover:text-[#16a34a] transition-colors">
                <Mail className="h-4 w-4 text-[#16a34a]" /> {CONTACT.email}
              </a>
              <a href={CONTACT.phoneHref} data-testid="footer-phone" className="flex items-center gap-2 text-sm font-semibold text-[#091630] hover:text-[#16a34a] transition-colors">
                <Phone className="h-4 w-4 text-[#16a34a]" /> {CONTACT.phone}
              </a>
              <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="flex items-center gap-2 text-sm font-semibold text-[#091630] hover:text-[#16a34a] transition-colors">
                <MessageCircle className="h-4 w-4 text-[#25D366]" /> WhatsApp business support
              </a>
              <p className="flex items-center gap-2 text-sm text-slate-500">
                <MapPin className="h-4 w-4 text-slate-400" /> {CONTACT.location}
              </p>
            </div>
            <button
              data-testid="footer-cta"
              onClick={() => goToAnchor(navigate, pathname, "#early-access")}
              className="mt-6 bg-[#21c55e] text-[#091630] font-bold text-sm py-2.5 px-5 rounded-full hover:bg-[#16a34a] hover:text-white transition-colors"
            >
              Join Early Access
            </button>
          </div>

          {/* Link columns */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-8">
            {/* Product */}
            <div className="flex flex-col gap-3">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Product</p>
              {NAV_LINKS.map((link) => {
                const tid = `footer-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`;
                if (link.type === "route") {
                  return <Link key={link.label} to={link.to} data-testid={tid} className={linkCls}>{link.label}</Link>;
                }
                if (link.type === "dropdown") {
                  return <Link key={link.label} to="/why-workpay/why-vendors-choose-workpay" data-testid={tid} className={linkCls}>{link.label}</Link>;
                }
                return (
                  <a key={link.label} href={`/${link.href}`} data-testid={tid} className={linkCls} onClick={(e) => { e.preventDefault(); handleLink(link); }}>
                    {link.label}
                  </a>
                );
              })}
            </div>

            {/* Company */}
            <div className="flex flex-col gap-3">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Company</p>
              <Link to="/about" data-testid="footer-company-about" className={linkCls}>About WorkPay</Link>
              <Link to="/about" data-testid="footer-company-founder" className={linkCls}>Founder</Link>
              <Link to="/contact" data-testid="footer-company-contact" className={linkCls}>Contact</Link>
              <a href={STORES.googlePlay} target="_blank" rel="noopener noreferrer" data-testid="footer-company-googleplay" className={`${linkCls} flex items-center gap-1.5`}><Store className="h-3.5 w-3.5" /> Google Play</a>
              <a href={STORES.appStore} target="_blank" rel="noopener noreferrer" data-testid="footer-company-appstore" className={`${linkCls} flex items-center gap-1.5`}><Apple className="h-3.5 w-3.5" /> App Store</a>
              <a href={FOUNDER.linkedin} target="_blank" rel="noopener noreferrer" data-testid="footer-company-linkedin" className={`${linkCls} flex items-center gap-1.5`}><Linkedin className="h-3.5 w-3.5" /> LinkedIn</a>
            </div>

            {/* Legal */}
            <div className="flex flex-col gap-3">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Legal</p>
              {LEGAL_LINKS.map((link) => (
                <Link key={link.to} to={link.to} data-testid={`footer-legal-${link.to.replace(/\//g, "")}`} className={linkCls}>
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-100 space-y-3">
          <p data-testid="footer-legal-operator" className="text-xs text-slate-500">
            {LEGAL_OPERATOR.sentence} {LEGAL_OPERATOR.freeZone} · Licence No. {LEGAL_OPERATOR.license} · TRN {LEGAL_OPERATOR.trn}.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <p className="text-xs text-slate-400">© {new Date().getFullYear()} WorkPay. All rights reserved.</p>
            <p className="text-xs text-slate-400 max-w-2xl sm:text-right">
              WorkPay is not a bank, wallet, payment gateway, or escrow provider. Payments are processed through Stripe.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
